import { Box, Theme, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import {
 focusPopoutWindow,
 usePoppedOutIds,
} from '../../../hooks/popoutLiveness';
import useScaleWidgets from '../../../hooks/useScaleWidgets';
import useWeighing from '../../../hooks/useWeighing';
import { WidgetOnlineWeighingProps } from '../../dashboard/Dashboard.types';

const statusColor = (theme: Theme, status: string) => {
 if (status === 'connected') return theme.palette.kemek.green;
 if (status === 'error') return theme.palette.kemek.red;
 return theme.palette.kemek.gray;
};

const NavScaleItem = ({
 widget,
 poppedOut,
}: { widget: WidgetOnlineWeighingProps; poppedOut: boolean }) => {
 const navigate = useNavigate();
 const scaleId = widget.scale?.Id ?? null;
 const w = useWeighing(scaleId);

 // Widget lives in its own window: front it and stay on the current page.
 const goToWidget = () => {
  if (poppedOut && widget.id) {
   focusPopoutWindow(widget.id);
   return;
  }
  navigate('/dashboard', { state: { highlightWidgetId: widget.id } });
 };

 const name = w.scaleName || widget.scale?.Name || widget.title || '—';
 const weight = w.live.weight == null ? '–' : String(w.live.weight);

 return (
  <Tooltip title={name}>
   <Box
    role="button"
    tabIndex={0}
    onClick={goToWidget}
    onKeyDown={(e) => {
     if (e.key === 'Enter' || e.key === ' ') goToWidget();
    }}
    sx={{
     display: 'flex',
     alignItems: 'center',
     gap: 1,
     p: 1,
     pr: '4px',
     height: 32,
     borderRadius: '16px',
     lineHeight: '18px',
     fontWeight: 500,
     cursor: 'pointer',
     border: (theme: Theme) => `1px solid ${theme.palette.kemek.lightGray}`,
     '&:hover': {
      borderColor: (theme: Theme) => theme.palette.kemek.blue,
     },
    }}
   >
    <Box
     sx={{
      width: 8,
      height: 8,
      borderRadius: '50%',
      flexShrink: 0,
      backgroundColor: (theme: Theme) => statusColor(theme, w.status),
     }}
    />
    <Box
     sx={{
      maxWidth: 90,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      color: (theme: Theme) => theme.palette.kemek.darkGray,
      fontSize: 12,
     }}
    >
     {name}
    </Box>
    <Box
     sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '24px',
      height: '24px',
      px: '6px',
      borderRadius: '24px',
      color: (theme: Theme) => theme.palette.kemek.white,
      background: (theme: Theme) =>
       w.live.stable ? theme.palette.kemek.green : theme.palette.kemek.gray,
      fontSize: 13,
      fontWeight: 600,
      fontVariantNumeric: 'tabular-nums',
     }}
    >
     {weight}
    </Box>
   </Box>
  </Tooltip>
 );
};

/** Live weight pills for each online-weighing widget the user has on dashboard. */
const NavScaleFeed = () => {
 const widgets = useScaleWidgets();
 const poppedOutIds = usePoppedOutIds();
 if (widgets.length === 0) return null;

 return (
  <Box
   sx={{
    display: { xs: 'none', md: 'flex' },
    alignItems: 'center',
    gap: '12px',
   }}
  >
   {widgets.map((w) => (
    <NavScaleItem
     key={w.id}
     widget={w}
     poppedOut={!!w.id && poppedOutIds.includes(w.id)}
    />
   ))}
  </Box>
 );
};

export default NavScaleFeed;
